/**
 * Enhanced validation utilities with Brazilian-specific rules
 * Comprehensive error handling and real-time validation feedback
 */

// Brazilian document validation utilities
export const BrazilianValidation = {
  // CPF validation with comprehensive checks
  validateCPF(cpf: string): { isValid: boolean; error?: string } {
    // Remove all non-numeric characters
    const cleanCPF = cpf.replace(/\D/g, "");

    // Check basic format
    if (cleanCPF.length !== 11) {
      return { isValid: false, error: "CPF deve ter 11 dígitos" };
    }

    // Check for repeated digits
    if (/^(\d)\1{10}$/.test(cleanCPF)) {
      return {
        isValid: false,
        error: "CPF não pode ter todos os dígitos iguais",
      };
    }

    // Validate check digits using Luhn algorithm
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += Number.parseInt(cleanCPF.charAt(i), 10) * (10 - i);
    }
    let checkDigit1 = (sum * 10) % 11;
    if (checkDigit1 === 10) {
      checkDigit1 = 0;
    }

    if (checkDigit1 !== Number.parseInt(cleanCPF.charAt(9), 10)) {
      return {
        isValid: false,
        error: "CPF inválido - primeiro dígito verificador incorreto",
      };
    }

    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += Number.parseInt(cleanCPF.charAt(i), 10) * (11 - i);
    }
    let checkDigit2 = (sum * 10) % 11;
    if (checkDigit2 === 10) {
      checkDigit2 = 0;
    }

    if (checkDigit2 !== Number.parseInt(cleanCPF.charAt(10), 10)) {
      return {
        isValid: false,
        error: "CPF inválido - segundo dígito verificador incorreto",
      };
    }

    return { isValid: true };
  },

  // CNPJ validation with comprehensive checks
  validateCNPJ(cnpj: string): { isValid: boolean; error?: string } {
    // Remove all non-numeric characters
    const cleanCNPJ = cnpj.replace(/\D/g, "");

    // Check basic format
    if (cleanCNPJ.length !== 14) {
      return { isValid: false, error: "CNPJ deve ter 14 dígitos" };
    }

    // Check for repeated digits
    if (/^(\d)\1{13}$/.test(cleanCNPJ)) {
      return {
        isValid: false,
        error: "CNPJ não pode ter todos os dígitos iguais",
      };
    }

    // Validate first check digit
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      const weightItem1 = weights1[i];
      if (weightItem1) {
        sum += Number.parseInt(cleanCNPJ.charAt(i), 10) * weightItem1;
      }
    }
    let checkDigit1 = sum % 11;
    checkDigit1 = checkDigit1 < 2 ? 0 : 11 - checkDigit1;

    if (checkDigit1 !== Number.parseInt(cleanCNPJ.charAt(12), 10)) {
      return {
        isValid: false,
        error: "CNPJ inválido - primeiro dígito verificador incorreto",
      };
    }

    // Validate second check digit
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    sum = 0;
    for (let i = 0; i < 13; i++) {
      const weightItem1 = weights2[i];

      if (weightItem1) {
        sum += Number.parseInt(cleanCNPJ.charAt(i), 10) * weightItem1;
      }
    }
    let checkDigit2 = sum % 11;
    checkDigit2 = checkDigit2 < 2 ? 0 : 11 - checkDigit2;

    if (checkDigit2 !== Number.parseInt(cleanCNPJ.charAt(13), 10)) {
      return {
        isValid: false,
        error: "CNPJ inválido - segundo dígito verificador incorreto",
      };
    }

    return { isValid: true };
  },

  // CEP validation
  validateCEP(cep: string): { isValid: boolean; error?: string } {
    const cleanCEP = cep.replace(/\D/g, "");

    if (cleanCEP.length !== 8) {
      return { isValid: false, error: "CEP deve ter 8 dígitos" };
    }

    // Check for obviously invalid CEPs
    if (/^0{8}$/.test(cleanCEP)) {
      return { isValid: false, error: "CEP inválido" };
    }

    return { isValid: true };
  },

  // Brazilian phone validation
  validatePhone(phone: string): {
    isValid: boolean;
    error?: string;
    type?: "mobile" | "landline";
  } {
    const cleanPhone = phone.replace(/\D/g, "");

    if (cleanPhone.length < 10 || cleanPhone.length > 11) {
      return { isValid: false, error: "Telefone deve ter 10 ou 11 dígitos" };
    }

    // Mobile phone validation (11 digits, 9 as third digit)
    if (cleanPhone.length === 11) {
      const areaCode = cleanPhone.substring(0, 2);
      const firstDigit = cleanPhone.charAt(2);

      // Valid area codes
      const validAreaCodes = [
        "11",
        "12",
        "13",
        "14",
        "15",
        "16",
        "17",
        "18",
        "19", // SP
        "21",
        "22",
        "24", // RJ
        "27",
        "28", // ES
        "31",
        "32",
        "33",
        "34",
        "35",
        "37",
        "38", // MG
        "41",
        "42",
        "43",
        "44",
        "45",
        "46", // PR
        "47",
        "48",
        "49", // SC
        "51",
        "53",
        "54",
        "55", // RS
        "61", // DF
        "62",
        "64", // GO
        "63", // TO
        "65",
        "66", // MT
        "67", // MS
        "68", // AC
        "69", // RO
        "71",
        "73",
        "74",
        "75",
        "77", // BA
        "79", // SE
        "81",
        "87", // PE
        "82", // AL
        "83", // PB
        "84", // RN
        "85",
        "88", // CE
        "86",
        "89", // PI
        "91",
        "93",
        "94", // PA
        "92",
        "97", // AM
        "95", // RR
        "96", // AP
        "98",
        "99", // MA
      ];

      if (!validAreaCodes.includes(areaCode)) {
        return { isValid: false, error: "Código de área inválido" };
      }

      if (firstDigit !== "9") {
        return {
          isValid: false,
          error: "Celular deve começar com 9 após o código de área",
        };
      }

      return { isValid: true, type: "mobile" };
    }

    // Landline validation (10 digits)
    if (cleanPhone.length === 10) {
      const firstDigit = cleanPhone.charAt(2);

      if (!["2", "3", "4", "5"].includes(firstDigit)) {
        return {
          isValid: false,
          error: "Telefone fixo deve começar com 2, 3, 4 ou 5",
        };
      }

      return { isValid: true, type: "landline" };
    }

    return { isValid: false, error: "Formato de telefone inválido" };
  },

  // Email validation with Brazilian domain considerations
  validateEmail(email: string): { isValid: boolean; error?: string } {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      return { isValid: false, error: "Formato de email inválido" };
    }

    // Additional checks for common issues
    if (email.includes("..")) {
      return {
        isValid: false,
        error: "Email não pode ter pontos consecutivos",
      };
    }

    if (email.startsWith(".") || email.endsWith(".")) {
      return {
        isValid: false,
        error: "Email não pode começar ou terminar com ponto",
      };
    }

    return { isValid: true };
  },
} as const;
